import { BooleanEnum, LocationLevelEnum, StoreEnum, ThemeEnum, VisualizationTypeEnum } from "@/enums";
import useRevitalizationStore from "@/modules/infographic/data/Revitalization";
import { IBoundary, IDashboardValuesPHC, IMaybe, INotSure } from "@/types";
import {
  boundaryDS,
  clientDetail,
  clientLeftNavIsOpen,
  clientTheme,
  clientToken,
  clientVisualizationType,
  dashboardDS,
  lastUpdated,
  locationBoundaryDS,
  maxValueDS,
} from "@/utils/Constant";
import {
  getIdxValue,
  getValue,
  removeIdxValue,
  removeValue,
} from "@/utils/Storage";
import { create, StateCreator } from "zustand";
import { devtools } from "zustand/middleware";
import {
  useHFStore,
  useIndicatorStore,
  useLayerStore,
  useLGAStore,
  useStateStore,
  useWardStore,
  useZoneStore,
} from "../Stores";

export interface UtilState {
  isAuthenticated: boolean;
  user: IMaybe<string>;
  userId: IMaybe<string>;
  token: IMaybe<string>;
  theme: ThemeEnum;
  rightNavIsOpen: boolean;
  leftNavIsOpen: boolean;
  isPHCView: boolean;
  isLoading: boolean;
  visualizationType: VisualizationTypeEnum;
  rightDescription: boolean;
  searchPhcText: string;
}

const getInitialState = (): Required<
  Pick<
    UtilState,
    | "isAuthenticated"
    | "user"
    | "userId"
    | "token"
    | "theme"
    | "rightNavIsOpen"
    | "leftNavIsOpen"
    | "visualizationType"
  >
> => {
  const _user = getValue(clientDetail) ?? null;
  const user = _user ? JSON.parse(_user) : null;
  const token = getValue(clientToken) ?? null;
  const theme = (getValue(clientTheme) as ThemeEnum) ?? ThemeEnum.DARK;
  // const isAuthenticated = (!!user?.username && !!token) || (false as boolean);
  const isAuthenticated = !!token || (false as boolean);
  const visualizationType =
    (getValue(clientVisualizationType) as VisualizationTypeEnum) ??
    VisualizationTypeEnum.MAP;
  const showLeftNav = JSON.parse(
    getValue(clientLeftNavIsOpen) ?? BooleanEnum.TRUE
  );

  if (!isAuthenticated) {
    removeValue(clientToken);
    removeValue(clientDetail);
  }

  return {
    isAuthenticated,
    user: isAuthenticated && user ? user?.username : null,
    userId: isAuthenticated && user ? user?.username : null,
    token: isAuthenticated ? token : null,
    theme: theme ?? ThemeEnum.DARK,
    rightNavIsOpen: false, //showRightNav ?? false,
    leftNavIsOpen: showLeftNav ?? true,
    visualizationType,
  };
};

export interface UtilActions {
  login: (user: INotSure) => void;
  logout: () => void;
  toggleTheme: (value?: ThemeEnum) => void;
  toggleRightNav: (rightNavIsOpen?: boolean) => void;
  toggleLeftNav: (payload?: boolean) => void;
  setIsPHCView: (isPHCView: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  resetApplication: () => void;
  setVisualizationTypeEnum: ({
    visualizationType,
    showRightNav,
  }: {
    visualizationType: VisualizationTypeEnum;
    showRightNav: boolean;
  }) => void;
  setRightDescription: (rightDescription: boolean) => void;
  idxLoad: () => Promise<void>;
  clearIdxStore: () => void;
  setSearchPhcText: (searchPhcText: string) => void;
}

const storeCreator: StateCreator<UtilState & UtilActions> = (set) => ({
  ...getInitialState(),
  isLoading: false,
  rightDescription: true,
  isPHCView: false,
  searchPhcText: "",
  setIsLoading: (isLoading) => set({ isLoading }),
  setSearchPhcText: (searchPhcText) => set({ searchPhcText }),
  login: (user) => {
    set({
      isAuthenticated: true,
      user: user?.username,
      userId: user?.token,
      token: user?.token,
    });
  },
  logout: () => {
    set({ isAuthenticated: false, user: null, userId: null, token: null });
    removeValue(clientToken);
    removeValue(clientDetail);
  },
  toggleTheme: (value?: ThemeEnum) =>
    set((state) => ({
      theme:
        value ??
        (state.theme === ThemeEnum.LIGHT ? ThemeEnum.DARK : ThemeEnum.LIGHT),
    })),
  toggleRightNav: (payload) =>
    set((state) => ({
      rightNavIsOpen: payload ?? !state.rightNavIsOpen,
    })),
  toggleLeftNav: (payload) =>
    set((state) => ({ leftNavIsOpen: payload ?? !state.leftNavIsOpen })),
  setIsPHCView: (isPHCView) => set({ isPHCView }),
  resetApplication: () => {
    const { setSelectedIndicator } = useIndicatorStore.getState();
    const { setSelectedZone } = useZoneStore.getState();
    const { setSelectedState, setStateList } = useStateStore.getState();
    const { setSelectedLga } = useLGAStore.getState();
    const { setSelectedWard } = useWardStore.getState();
    const { setSelectedHF } = useHFStore.getState();

    setSelectedZone(null);
    setSelectedState(null);
    setSelectedLga(null);
    setSelectedWard(null);
    setSelectedHF(null);
    setStateList([]);
    setSelectedIndicator([]);
  },
  idxLoad: async () => {
    const { loadBoundary, setMaxValue, resetLayer } =
      useLayerStore.getState();
    const { setDashboardData, setIndicatorData } = useIndicatorStore.getState();
    const { clearIdxStore, setIsLoading } =
      useUtilStore.getState();
      const { setRvQuery } = useRevitalizationStore.getState();

    if (!window.indexedDB) return;
    setIsLoading(true);
    const [_boundaryData, _dashboardData, _maxValueData] = await Promise.all([
      getIdxValue(boundaryDS) as Promise<IMaybe<IBoundary[]>>,
      getIdxValue(dashboardDS) as Promise<IMaybe<IDashboardValuesPHC>>,
      getIdxValue(maxValueDS) as Promise<IMaybe<number>>,
    ]);

    // check if any of them is null then remove the data from the store
    if (!_boundaryData || !_dashboardData || !_maxValueData)
      return clearIdxStore();

    if (_boundaryData && Array.isArray(_boundaryData)) {
      resetLayer();
      loadBoundary(_boundaryData, LocationLevelEnum.COUNTRY);
      setIndicatorData(_boundaryData[0] ?? null);
    }

    if (_dashboardData) setDashboardData(_dashboardData);
    if (_maxValueData) setMaxValue(_maxValueData);

    setRvQuery("")
    setIsLoading(false);
  },
  clearIdxStore: async () => {
    const { resetSelections } = useZoneStore.getState();
    const { resetLayer } = useLayerStore.getState();

    resetSelections();
    resetLayer();

    await Promise.all([
      removeIdxValue(dashboardDS),
      removeIdxValue(maxValueDS),
      removeIdxValue(boundaryDS),
      removeIdxValue(locationBoundaryDS),
      removeIdxValue(lastUpdated),
    ]);

    window.location.reload();
  },
  setVisualizationTypeEnum: ({ visualizationType, showRightNav }) =>
    set((state) => ({
      visualizationType,
      rightNavIsOpen: showRightNav ?? state.rightNavIsOpen,
    })),
  setRightDescription: (rightDescription) => set({ rightDescription }),
});

const useUtilStore = create<UtilState & UtilActions>(
  devtools(storeCreator, { name: StoreEnum.AUTH }) as StateCreator<
    UtilState & UtilActions
  >
);

export default useUtilStore;
